import { ButtonLink, Divider, Pill, Spinner, Stack } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { Card } from '@/components/widgets/o/Card/Card';
import { useOrganizationWorkstream } from '@/hooks/useOrganizationWorkstream';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';

export const WorkstreamPill = () => {
  const workstreamId = useWorkstreamId();
  const { t } = useTranslation();

  if (!workstreamId) {
    return null;
  }

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('global.workstream.labels.selectedWorkstream')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Selected workstream rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <WorkstreamPillBase id={workstreamId} />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const WorkstreamPillBase = ({ id }: { id: string }) => {
  const workstream = useOrganizationWorkstream(id).data;
  const { t } = useTranslation();
  const paths = usePathSet();

  return (
    <Pill
      className='border border-divider pl-3 pr-2 py-1 bg-neutral-white-245'
      leftAdornment={<i className='ri-filter-3-fill' data-testid='workstream-pill-icon' />}
      rightAdornment={
        <ButtonLink to={paths.home} variant='text' className='p-1 bg-primary-12'>
          <i className='ri-close-line' />
        </ButtonLink>
      }
    >
      <Divider direction='vertical' />
      <Stack direction='row' centerMain spacing={2}>
        <span className='typography-overline text-primary-100 text-xs'>{t('global.workstream.labels.scope')}</span>
        <span className='typography-body2Semibold text-text-primary'>{workstream.title}</span>
      </Stack>
    </Pill>
  );
};
